import React, { useState, useContext } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import { IoCardOutline, IoWalletOutline } from "react-icons/io5";
import { FiSend, FiLoader } from "react-icons/fi";
import { CartContext } from "../context/CartProvider";

const Purchase = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { clearCart } = useContext(CartContext);
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("worldpay");
  const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:3000/api/";

  const fullFormData = location.state?.fullFormData;

  if (!fullFormData || !fullFormData.items || !fullFormData.userInfo) {
    return <Navigate to="/checkout" replace />;
  }

  const { items, userInfo } = fullFormData;

  const handlePayment = async () => {
    setLoading(true);

    if (paymentMethod === "worldpay") {
      try {
        const amountInPence = Math.round(parseFloat(userInfo?.payableAmount || 0) * 100);
        
        const response = await axios.post(
          `${baseUrl}worldpay/create-session`,
          {
            amount: amountInPence,
            currency: "GBP",
            userId: userInfo?.userId,
            email: userInfo?.email,
            items,
            userInfo
          }
        );
        
        if (response.data.success && response.data.checkoutUrl) {
          window.location.href = response.data.checkoutUrl;
        } else {
          toast.error(response.data.error || "Could not create payment session.");
        }
      } catch (error) {
        console.log(error);
        toast.error(
          error.response?.data?.error || "Something went wrong with payment."
        );
      } finally {
        setLoading(false);
      }
    } else if (paymentMethod === "cod") {
      try {
        const response = await axios.post(`${baseUrl}order/cod`, {
          items,
          userInfo,
        });
        if (response.data.success) {
          Swal.fire({
            title: "Order Placed!",
            text: "Your Cash on Delivery order has been successfully placed.",
            icon: "success",
            confirmButtonColor: "#e5272e",
          }).then(() => {
            clearCart();
            localStorage.removeItem("checkoutFormSimplified");
            navigate("/myaccount", { state: { toggle: 2 } });
          });
        } else {
          toast.error(response.data.message || "Failed to place COD order.");
        }
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to place COD order."
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const primaryAccentColor = "#e5272e";

  return (
    <div
      style={{
        minHeight: "80vh",
        backgroundColor: "#f0f2f5",
        paddingTop: "60px",
        paddingBottom: "60px",
        display: "flex",
        alignItems: "center",
      }}
    >
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-10 col-lg-8 col-xl-7">
            <div
              className="card"
              style={{
                backgroundColor: "#ffffff",
                borderRadius: "12px",
                boxShadow: "0 8px 25px rgba(0, 0, 0, 0.1)",
                overflow: "hidden",
              }}
            >
              <div className="card-body p-4 p-md-5">
                <h3
                  className="fw-semibold text-center mb-4"
                  style={{ color: "#333" }}
                >
                  Complete Your Payment
                </h3>
                <p className="text-center text-muted mb-4">
                  Total Payable:
                  <strong
                    style={{ color: primaryAccentColor, fontSize: "1.2em" }}
                  >
                    {" "}
                    £{parseFloat(userInfo?.payableAmount || 0).toFixed(2)}
                  </strong>
                </p>

                <div className="mb-4">
                  <p className="text-muted text-center mb-3 small fw-medium">
                    Select Payment Method
                  </p>
                  <div className="d-grid gap-3">
                    <div className="form-check">
                      <input
                        type="radio"
                        id="worldpayPayment"
                        name="paymentMethod"
                        value="worldpay"
                        checked={paymentMethod === "worldpay"}
                        onChange={() => setPaymentMethod("worldpay")}
                        className="form-check-input"
                        style={{ display: "none" }}
                      />
                      <label
                        htmlFor="worldpayPayment"
                        className="form-check-label"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "0.75rem 1rem",
                          border: `2px solid ${paymentMethod === "worldpay"
                            ? primaryAccentColor
                            : "#dee2e6"
                            }`,
                          borderRadius: "8px",
                          cursor: "pointer",
                          transition: "all 0.2s ease-in-out",
                          backgroundColor:
                            paymentMethod === "worldpay"
                              ? "rgba(229, 39, 46, 0.05)"
                              : "#fff",
                          boxShadow:
                            paymentMethod === "worldpay"
                              ? `0 0 0 0.1rem rgba(229, 39, 46, 0.25)`
                              : "none",
                        }}
                      >
                        <IoCardOutline
                          size={24}
                          style={{
                            marginRight: "10px",
                            color:
                              paymentMethod === "worldpay"
                                ? primaryAccentColor
                                : "#6c757d",
                          }}
                        />
                        Pay with Card
                      </label>
                    </div>
                  
                    <div className="form-check">
                      <input
                        type="radio"
                        id="codPayment"
                        name="paymentMethod"
                        value="cod"
                        checked={paymentMethod === "cod"}
                        onChange={() => setPaymentMethod("cod")}
                        className="form-check-input"
                        style={{ display: "none" }}
                      />
                      <label
                        htmlFor="codPayment"
                        className="form-check-label"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "0.75rem 1rem",
                          border: `2px solid ${paymentMethod === "cod"
                            ? primaryAccentColor
                            : "#dee2e6"
                            }`,
                          borderRadius: "8px",
                          cursor: "pointer",
                          transition: "all 0.2s ease-in-out",
                          backgroundColor:
                            paymentMethod === "cod"
                              ? "rgba(229, 39, 46, 0.05)"
                              : "#fff",
                          boxShadow:
                            paymentMethod === "cod"
                              ? `0 0 0 0.1rem rgba(229, 39, 46, 0.25)`
                              : "none",
                        }}
                      >
                        <IoWalletOutline
                          size={24}
                          style={{
                            marginRight: "10px",
                            color:
                              paymentMethod === "cod"
                                ? primaryAccentColor
                                : "#6c757d",
                          }}
                        />
                        Cash on Delivery
                      </label>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handlePayment}
                  disabled={loading}
                  className="btn w-100 mt-4"
                  style={{
                    backgroundImage:
                      "linear-gradient(to right, #e5272e, #f59332)",
                    color: "white",
                    padding: "0.8rem 1.5rem",
                    borderRadius: "50px",
                    fontWeight: "600",
                  }}
                >
                  {loading ? (
                    <>
                      <FiLoader
                        style={{ animation: "spin 1s linear infinite" }}
                      />{" "}
                      Processing...
                    </>
                  ) : (
                    <>
                      <FiSend className="me-2" />{" "}
                      {paymentMethod === "worldpay"
                        ? `Pay Securely £${parseFloat(
                          userInfo?.payableAmount || 0
                        ).toFixed(2)}`
                        : "Place Order (COD)"}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Purchase;
